import React, {useContext, useEffect, useRef, useState} from "react";
import Reveal from "../../components/reveal/Reveal";
import "./Greeting.scss";
import SocialMedia from "../../components/socialMedia/SocialMedia";
import Button from "../../components/button/Button";
import DisplayLottie from "../../components/displayLottie/DisplayLottie";
import landingPerson from "../../assets/lottie/landingPerson";
import {greeting, illustration, bigProjects} from "../../portfolio";
import StyleContext from "../../contexts/StyleContext";

const highlights = [
  "Node.js",
  "React",
  "Next.js",
  "Nuxt.js",
  "Angular",
  "AWS",
  "Stripe",
  "TypeScript"
];

const roles = [
  "Full-Stack Developer",
  "Node.js Engineer",
  "Nuxt & Vue Specialist",
  "React / Next.js",
  "AWS & DevOps"
];

const orbitIcons = [
  {icon: "fab fa-react", label: "React"},
  {icon: "fab fa-node-js", label: "Node"},
  {icon: "fab fa-vuejs", label: "Vue"},
  {icon: "fab fa-aws", label: "AWS"},
  {icon: "fab fa-js", label: "TS"},
  {icon: "fas fa-credit-card", label: "Stripe"}
];

const CODE_LINES = [
  {html: true, parts: [{t: "kw", v: "const"}, {t: "plain", v: " umair = {"}]},
  {
    html: true,
    parts: [
      {t: "plain", v: "  role: "},
      {t: "str", v: '"Full-Stack"'},
      {t: "plain", v: ","}
    ]
  },
  {html: true, parts: [{t: "plain", v: "  stack: ["}]},
  {
    html: true,
    parts: [
      {t: "plain", v: "    "},
      {t: "str", v: '"Node"'},
      {t: "plain", v: ", "},
      {t: "str", v: '"Nuxt"'},
      {t: "plain", v: ", "},
      {t: "str", v: '"AWS"'}
    ]
  },
  {html: true, parts: [{t: "plain", v: "  ],"}]},
  {
    html: true,
    parts: [
      {t: "plain", v: "  hireable: "},
      {t: "bool", v: "true"}
    ]
  },
  {html: true, parts: [{t: "plain", v: "};"}]}
];

function CountUp({to, suffix = "+", active}) {
  const [value, setValue] = useState(0);

  useEffect(() => {
    if (!active) {
      setValue(0);
      return;
    }
    let frame;
    const start = performance.now();
    const duration = 1200;
    const tick = now => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setValue(Math.round(to * eased));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [active, to]);

  return (
    <strong>
      {value}
      {suffix}
    </strong>
  );
}

function CodePart({t, v}) {
  if (t === "kw") return <span className="code-kw">{v}</span>;
  if (t === "str") return <span className="code-str">{v}</span>;
  if (t === "bool") return <span className="code-bool">{v}</span>;
  return <span>{v}</span>;
}

export default function Greeting() {
  const {isDark} = useContext(StyleContext);
  const visualRef = useRef(null);
  const frameRef = useRef(null);
  const orbitRef = useRef(null);
  const parallaxRaf = useRef(null);
  const [roleIndex, setRoleIndex] = useState(0);
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [codeLine, setCodeLine] = useState(0);
  const [statsActive, setStatsActive] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setStatsActive(true), 600);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (codeLine >= CODE_LINES.length) return undefined;
    const t = setTimeout(() => setCodeLine(n => n + 1), 320);
    return () => clearTimeout(t);
  }, [codeLine]);

  useEffect(() => {
    const visual = visualRef.current;
    const frame = frameRef.current;
    const orbit = orbitRef.current;
    if (!visual || !frame) return;

    const onMove = e => {
      if (parallaxRaf.current) return;
      const {clientX, clientY} = e;
      parallaxRaf.current = requestAnimationFrame(() => {
        parallaxRaf.current = null;
        const rect = visual.getBoundingClientRect();
        const x = (clientX - rect.left) / rect.width - 0.5;
        const y = (clientY - rect.top) / rect.height - 0.5;
        frame.style.transform = `translate3d(${x * 18}px, ${y * 12}px, 0) rotateY(${
          x * 4
        }deg) rotateX(${-y * 3}deg)`;
        if (orbit) {
          orbit.style.transform = `rotate(${x * 5}deg)`;
        }
      });
    };

    const onLeave = () => {
      frame.style.transform = "translate3d(0, 0, 0) rotateY(0) rotateX(0)";
      if (orbit) orbit.style.transform = "rotate(0deg)";
    };

    visual.addEventListener("mousemove", onMove);
    visual.addEventListener("mouseleave", onLeave);
    return () => {
      if (parallaxRaf.current) cancelAnimationFrame(parallaxRaf.current);
      visual.removeEventListener("mousemove", onMove);
      visual.removeEventListener("mouseleave", onLeave);
    };
  }, []);

  useEffect(() => {
    const current = roles[roleIndex];
    const speed = deleting ? 40 : 70;
    const pause = deleting ? 400 : 1600;

    if (!deleting && typed === current) {
      const t = setTimeout(() => setDeleting(true), pause);
      return () => clearTimeout(t);
    }
    if (deleting && typed === "") {
      setDeleting(false);
      setRoleIndex(i => (i + 1) % roles.length);
      return;
    }

    const t = setTimeout(() => {
      setTyped(
        deleting
          ? current.slice(0, typed.length - 1)
          : current.slice(0, typed.length + 1)
      );
    }, speed);
    return () => clearTimeout(t);
  }, [typed, deleting, roleIndex]);

  if (!greeting.displayGreeting) {
    return null;
  }

  const projectCount =
    bigProjects && bigProjects.projects ? bigProjects.projects.length : 5;

  return (
    <Reveal direction="up">
      <div className="greet-main" id="greeting">
        <div className="greeting-main">
          <div className="greeting-text-div">
            <div className="greeting-status">
              <span className="greeting-eyebrow">Available for opportunities</span>
              <span className="greeting-location">
                <i className="fas fa-map-marker-alt" aria-hidden="true" />
                Lahore, PK
              </span>
            </div>

            <p className="greeting-hello">Hello, I&apos;m</p>
            <h1 className="greeting-text">
              {greeting.title}
              <span className="greeting-text-underline" aria-hidden="true" />
            </h1>

            <p className="greeting-role">
              <span className="greeting-role-prefix">I build as a </span>
              <span className="greeting-role-typed">
                {typed}
                <span className="greeting-caret" />
              </span>
            </p>

            <p
              className={
                isDark ? "greeting-text-p" : "greeting-text-p subTitle"
              }
            >
              {greeting.subTitle}
            </p>

            <ul className="greeting-stats" aria-label="Highlights">
              <li style={{"--i": 0}}>
                <CountUp to={3} active={statsActive} />
                <span>Years exp.</span>
              </li>
              <li style={{"--i": 1}}>
                <CountUp to={projectCount} active={statsActive} />
                <span>Projects</span>
              </li>
              <li style={{"--i": 2}}>
                <CountUp to={10} active={statsActive} />
                <span>Technologies</span>
              </li>
            </ul>

            <ul className="greeting-highlights" aria-label="Core technologies">
              {highlights.map((item, i) => (
                <li key={item} style={{"--i": i}}>
                  {item}
                </li>
              ))}
            </ul>

            <SocialMedia />
            <div className="button-greeting-div">
              <Button text="Contact me" href="#contact" />
              {greeting.resumeLink && (
                <a
                  href={greeting.resumeLink}
                  download="Resume.pdf"
                  className="download-link-button"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button text="Download resume" variant="outline" />
                </a>
              )}
              <a href="#projects" className="greeting-ghost-link">
                View projects
                <span aria-hidden="true">→</span>
              </a>
            </div>
          </div>

          <div className="greeting-visual" ref={visualRef}>
            <div className="greeting-visual-glow" aria-hidden="true" />
            <div className="greeting-visual-ring" aria-hidden="true" />

            <div className="greeting-orbit" ref={orbitRef} aria-hidden="true">
              {orbitIcons.map((item, i) => (
                <span
                  key={item.label}
                  className={`orbit-icon orbit-icon-${i}`}
                  style={{"--i": i, "--total": orbitIcons.length}}
                  title={item.label}
                >
                  <i className={item.icon} />
                </span>
              ))}
            </div>

            <div className="greeting-lottie-frame" ref={frameRef}>
              {illustration.animated ? (
                <DisplayLottie animationData={landingPerson} speed={2.2} />
              ) : (
                <img
                  alt="Developer illustration"
                  src={require("../../assets/images/manOnTable.svg")}
                />
              )}
            </div>

            <div className="greeting-float-badge greeting-float-badge-1">
              <span className="badge-pulse" />
              <span>3+ years</span>
            </div>
            <div className="greeting-float-badge greeting-float-badge-2">
              <i className="fas fa-code" aria-hidden="true" />
              <span>Full-Stack</span>
            </div>
            <div className="greeting-float-badge greeting-float-badge-3">
              <span className="badge-pulse" />
              <span>Open to work</span>
            </div>

            <div className="greeting-code-card" aria-hidden="true">
              <div className="code-card-dots">
                <span />
                <span />
                <span />
              </div>
              <pre>
                <code>
                  {CODE_LINES.slice(0, codeLine).map((line, i) => (
                    <div key={i} className="code-line">
                      {line.parts.map((part, j) => (
                        <CodePart key={j} t={part.t} v={part.v} />
                      ))}
                    </div>
                  ))}
                  {codeLine < CODE_LINES.length && (
                    <div className="code-line code-line-active">
                      <span className="code-typing-caret" />
                    </div>
                  )}
                </code>
              </pre>
            </div>

            <ul className="greeting-mobile-chips" aria-hidden="true">
              {orbitIcons.map((item, i) => (
                <li
                  key={item.label}
                  className="greeting-mobile-chip"
                  style={{animationDelay: `${0.15 + i * 0.07}s`}}
                >
                  <i className={item.icon} />
                  {item.label}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <a href="#skills" className="greeting-scroll" aria-label="Scroll to skills">
          <span>Scroll</span>
          <span className="greeting-scroll-mouse">
            <span className="greeting-scroll-wheel" />
          </span>
        </a>
      </div>
    </Reveal>
  );
}
